import React, { useEffect, useState } from 'react';
import { CATALOG } from '@/lib/capabilities/catalog';
import { FUNNEL, SUPPORTING, returnedAfter, summariseMilestone, type ActivationRow } from './activation';
import {
  activationFunnel,
  editionMix,
  overrideSummary,
  usageSummary,
  type EditionMixRow,
  type OverrideSummaryRow,
  type UsageRow,
} from './api';

function title(capKey: string): string {
  const node = (CATALOG as Record<string, { title?: string }>)[capKey];
  return node?.title ?? capKey;
}

export default function Reporting() {
  const [mix, setMix] = useState<EditionMixRow[]>([]);
  const [overrides, setOverrides] = useState<OverrideSummaryRow[]>([]);
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [cohort, setCohort] = useState<ActivationRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const [m, o, u, c] = await Promise.all([editionMix(), overrideSummary(), usageSummary(30), activationFunnel(90)]);
      // Developer workspaces (tracker 47) are ours, not customers: kept out of activation and paid counts.
      const internal = new Set(m.filter((row) => row.is_internal).map((row) => row.plan_key));
      setCohort(c.filter((row) => !internal.has(row.edition_key ?? '')));
      setMix(m);
      setOverrides(o);
      setUsage(u);
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="muted">Loading reporting…</p>;

  const maxWs = Math.max(1, ...mix.map((m) => m.workspace_count));

  const hours = (h: number | null) => (h == null ? '-' : h < 48 ? `${h.toFixed(1)} h` : `${(h / 24).toFixed(1)} d`);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Activation (workspaces created in the last 90 days)</h2>
        <p className="sub">
          Read from the record, not from analytics, so users who have not answered the consent prompt are counted. The
          first-run sample asset is excluded. Primary activation metric: first inspection completed.
        </p>
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Milestone</th>
              <th className="col-ed">Workspaces</th>
              <th className="col-ed">% of {cohort.length}</th>
              <th className="col-ed">Median time from signup</th>
            </tr>
          </thead>
          <tbody>
            {[...FUNNEL, ...SUPPORTING].map(({ key, label }) => {
              const s = summariseMilestone(cohort, key, label);
              return (
                <tr key={key}>
                  <td>{label}</td>
                  <td className="col-ed">{s.reached}</td>
                  <td className="col-ed">{cohort.length ? Math.round((s.reached / cohort.length) * 100) : 0}%</td>
                  <td className="col-ed">{hours(s.medianHours)}</td>
                </tr>
              );
            })}
            {[1, 7, 30].map((d) => {
              const r = returnedAfter(cohort, d);
              return (
                <tr key={`d${d}`}>
                  <td>Returned on or after day {d}</td>
                  <td className="col-ed">{r.returned}</td>
                  <td className="col-ed">{r.eligible ? Math.round((r.returned / r.eligible) * 100) : 0}% of {r.eligible}</td>
                  <td className="col-ed">-</td>
                </tr>
              );
            })}
            <tr>
              <td>Paid</td>
              <td className="col-ed">{cohort.filter((c) => c.paid).length}</td>
              <td className="col-ed">{cohort.length ? Math.round((cohort.filter((c) => c.paid).length / cohort.length) * 100) : 0}%</td>
              <td className="col-ed">-</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Edition / revenue mix</h2>
        <p className="sub">
          Workspaces per edition, and the operational Assets plan. Developer is internal: never sold, and left out of
          the activation and paid numbers above.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          {mix.map((m) => (
            <div key={m.plan_key} className="row">
              <div style={{ width: 130 }}>
                {m.display_name}
                {m.is_internal ? <span className="pill" style={{ marginLeft: 6 }}>Internal</span> : null}
              </div>
              <div style={{ flex: 1 }}>
                <div className="bar" style={{ width: `${(m.workspace_count / maxWs) * 100}%`, minWidth: 2 }} />
              </div>
              <div style={{ width: 40, textAlign: 'right' }}>{m.workspace_count}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Configuration &amp; adoption</h2>
        <p className="sub">Active per-workspace/global overrides by capability - what admins have deviated from edition defaults.</p>
        {overrides.length === 0 ? (
          <p className="muted">No active overrides. Every workspace is on its edition defaults.</p>
        ) : (
          <table className="matrix" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Capability</th>
                <th className="col-ed">Effect</th>
                <th className="col-ed">Workspaces</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o, i) => (
                <tr key={i}>
                  <td>
                    <div className="feat-name">{title(o.capability_key)}</div>
                    <div className="feat-key">{o.capability_key}</div>
                  </td>
                  <td className="col-ed" style={{ color: o.effect === 'enable' ? 'var(--on)' : 'var(--danger)' }}>
                    {o.effect}
                  </td>
                  <td className="col-ed">{o.override_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Runtime feature usage (last 30 days)</h2>
        <p className="sub">
          Actual feature invocations per capability.
        </p>
        {usage.length === 0 ? (
          <p className="muted">
            No usage recorded yet. Runtime usage needs client instrumentation - the
            <code> capability_usage_events</code> table and <code>log_capability_usage()</code> RPC are in place;
            wiring the app to emit on feature use is the next phase.
          </p>
        ) : (
          <table className="matrix" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Capability</th>
                <th className="col-ed">Events</th>
                <th className="col-ed">Last used</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.capability_key}>
                  <td>
                    <div className="feat-name">{title(u.capability_key)}</div>
                    <div className="feat-key">{u.capability_key}</div>
                  </td>
                  <td className="col-ed">{u.event_count}</td>
                  <td className="col-ed">{new Date(u.last_used).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
